import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Check,
  ChevronRight,
  Clipboard,
  FilterX,
  Info,
  RefreshCw,
  X
} from 'lucide-react';
import Pagination from '../components/Pagination';
import {
  getTokenUsageBreakdown,
  getTokenUsageEvents,
  getTokenUsageFilters,
  getTokenUsageOverview
} from '../api/tokenUsageApi';
import { formatPatientName, normalizeUiLocale } from '../utils/locale';
import './TokenUsageDashboard.css';

const EMPTY_OPTIONS = {
  providers: [],
  models: [],
  purposes: [],
  clinics: [],
  stations: []
};

const RANGE_OPTIONS = [7, 30, 90];
const DIMENSIONS = ['clinic', 'station', 'patient', 'conversation', 'request', 'model', 'provider', 'purpose', 'day'];

const localDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const presetDates = (days) => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days + 1);
  return { from: localDateString(from), to: localDateString(to) };
};

const initialFilters = (searchParams) => {
  const range = searchParams.get('range') || '30';
  const defaults = presetDates(Number(range) || 30);
  return {
    range,
    from: searchParams.get('from_date') || defaults.from,
    to: searchParams.get('to_date') || defaults.to,
    clinic_id: searchParams.get('clinic_id') || '',
    station_id: searchParams.get('station_id') || '',
    patient_id: searchParams.get('patient_id') || '',
    provider: searchParams.get('provider') || '',
    model: searchParams.get('model') || '',
    purpose: searchParams.get('purpose') || '',
    status: searchParams.get('status') || '',
    usage_status: searchParams.get('usage_status') || '',
    conversation_id: searchParams.get('conversation_id') || '',
    request_id: searchParams.get('request_id') || ''
  };
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
  } catch (error) {
    return 'Asia/Shanghai';
  }
};

const toAPIParams = (filters) => {
  const fallback = presetDates(30);
  const from = new Date(`${filters.from || fallback.from}T00:00:00`);
  const to = new Date(`${filters.to || fallback.to}T00:00:00`);
  to.setDate(to.getDate() + 1);
  const params = {
    from: from.toISOString(),
    to: to.toISOString(),
    timezone: getTimezone()
  };
  Object.entries(filters).forEach(([key, value]) => {
    if (!['range', 'from', 'to'].includes(key) && value) {
      params[key] = value;
    }
  });
  return params;
};

const tokenValue = (value, formatter) => (
  value === null || value === undefined ? '--' : formatter.format(value)
);

const compactNumber = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value >= 100000 ? 0 : 1)}K`;
  }
  return String(value || 0);
};

const TokenUsageDashboard = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => initialFilters(searchParams));
  const [dimension, setDimension] = useState(searchParams.get('dimension') || 'clinic');
  const [eventPage, setEventPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('page_size')) || 25);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [overview, setOverview] = useState(null);
  const [breakdown, setBreakdown] = useState({ items: [], total: 0 });
  const [events, setEvents] = useState({ items: [], total: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale),
    [locale]
  );
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'medium'
    }),
    [locale]
  );

  useEffect(() => {
    getTokenUsageFilters().then(setOptions).catch(() => setOptions(EMPTY_OPTIONS));
  }, []);

  useEffect(() => {
    if (!selectedEvent) {
      return undefined;
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setSelectedEvent(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedEvent]);

  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      if (key === 'from' || key === 'to') {
        next.set(`${key}_date`, value);
      } else {
        next.set(key, value);
      }
    });
    next.set('dimension', dimension);
    if (eventPage > 1) next.set('page', String(eventPage));
    if (pageSize !== 25) next.set('page_size', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [dimension, eventPage, filters, pageSize, setSearchParams]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      const params = toAPIParams(filters);
      try {
        const [overviewData, breakdownData, eventData] = await Promise.all([
          getTokenUsageOverview(params),
          getTokenUsageBreakdown({
            ...params,
            dimension,
            limit: 10,
            offset: 0,
            sort: 'total_tokens',
            direction: 'desc'
          }),
          getTokenUsageEvents({
            ...params,
            limit: pageSize,
            offset: (eventPage - 1) * pageSize
          })
        ]);
        if (active) {
          setOverview(overviewData);
          setBreakdown(breakdownData);
          setEvents(eventData);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError?.response?.data?.error || t('tokenUsage.loadError'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [dimension, eventPage, filters, pageSize, refreshKey, t]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setEventPage(1);
  }, []);

  const applyPreset = (days) => {
    const dates = presetDates(days);
    setFilters((current) => ({ ...current, range: String(days), ...dates }));
    setEventPage(1);
  };

  const resetFilters = () => {
    setFilters({ ...initialFilters(new URLSearchParams()), ...presetDates(30), range: '30' });
    setDimension('clinic');
    setEventPage(1);
  };

  const copyValue = async (key, value) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(key);
    window.setTimeout(() => setCopied(''), 1200);
  };

  const totals = overview?.totals || {};
  const metrics = [
    ['total', t('tokenUsage.totalTokens'), totals.total_tokens],
    ['input', t('tokenUsage.inputTokens'), totals.input_tokens],
    ['output', t('tokenUsage.outputTokens'), totals.output_tokens],
    ['cache', t('tokenUsage.cacheRead'), totals.cache_read_tokens],
    ['requests', t('tokenUsage.requests'), totals.request_count],
    ['calls', t('tokenUsage.modelCalls'), totals.model_call_count]
  ];
  const coverage = Number(totals.usage_coverage_rate || 0);
  const maxBreakdown = Math.max(...(breakdown.items || []).map((item) => item.total_tokens || 0), 1);
  const getBreakdownLabel = (item) => (
    dimension === 'patient'
      ? formatPatientName(item, locale, item.label)
      : item.label
  );
  const totalPages = Math.max(1, Math.ceil((events.total || 0) / pageSize));

  return (
    <div className="admin-page token-usage-page">
      <header className="token-usage-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('tokenUsage.title')}</h1>
          <p className="page-subtitle">{t('tokenUsage.subtitle')}</p>
        </div>
        <div className="token-usage-header__actions">
          <span className={`token-coverage-badge ${coverage < 95 ? 'is-warning' : ''}`}>
            <Info size={15} aria-hidden="true" />
            {t('tokenUsage.coverage')} {coverage.toFixed(1)}%
          </span>
          <button
            type="button"
            className="token-icon-button"
            title={t('tokenUsage.refresh')}
            aria-label={t('tokenUsage.refresh')}
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            <RefreshCw size={18} className={loading ? 'is-spinning' : ''} />
          </button>
        </div>
      </header>

      <section className="token-filter-band" aria-label={t('tokenUsage.filters')}>
        <div className="token-range-control">
          {RANGE_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              className={filters.range === String(days) ? 'is-active' : ''}
              onClick={() => applyPreset(days)}
            >
              {days}{t('tokenUsage.days')}
            </button>
          ))}
        </div>
        <label>
          <span>{t('tokenUsage.from')}</span>
          <input
            type="date"
            className="theme-input"
            value={filters.from}
            max={filters.to}
            onChange={(event) => {
              updateFilter('from', event.target.value);
              updateFilter('range', 'custom');
            }}
          />
        </label>
        <label>
          <span>{t('tokenUsage.to')}</span>
          <input
            type="date"
            className="theme-input"
            value={filters.to}
            min={filters.from}
            onChange={(event) => {
              updateFilter('to', event.target.value);
              updateFilter('range', 'custom');
            }}
          />
        </label>
        <label>
          <span>{t('tokenUsage.clinic')}</span>
          <select className="theme-select" value={filters.clinic_id} onChange={(event) => updateFilter('clinic_id', event.target.value)}>
            <option value="">{t('tokenUsage.allClinics')}</option>
            {options.clinics.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          <span>{t('tokenUsage.station')}</span>
          <select className="theme-select" value={filters.station_id} onChange={(event) => updateFilter('station_id', event.target.value)}>
            <option value="">{t('tokenUsage.allStations')}</option>
            {options.stations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          <span>{t('tokenUsage.provider')}</span>
          <select className="theme-select" value={filters.provider} onChange={(event) => updateFilter('provider', event.target.value)}>
            <option value="">{t('tokenUsage.allProviders')}</option>
            {options.providers.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span>{t('tokenUsage.model')}</span>
          <select className="theme-select" value={filters.model} onChange={(event) => updateFilter('model', event.target.value)}>
            <option value="">{t('tokenUsage.allModels')}</option>
            {options.models.map((item) => <option key={`${item.value}-${item.label}`} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <details className="token-advanced-filters">
          <summary>{t('tokenUsage.moreFilters')}</summary>
          <div className="token-advanced-filters__body">
            <label><span>{t('tokenUsage.patientId')}</span><input className="theme-input" type="number" min="1" value={filters.patient_id} onChange={(event) => updateFilter('patient_id', event.target.value)} /></label>
            <label><span>{t('tokenUsage.conversation')}</span><input className="theme-input" value={filters.conversation_id} onChange={(event) => updateFilter('conversation_id', event.target.value)} /></label>
            <label><span>{t('tokenUsage.request')}</span><input className="theme-input" value={filters.request_id} onChange={(event) => updateFilter('request_id', event.target.value)} /></label>
            <label><span>{t('tokenUsage.purpose')}</span><select className="theme-select" value={filters.purpose} onChange={(event) => updateFilter('purpose', event.target.value)}><option value="">{t('tokenUsage.allPurposes')}</option>{options.purposes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <label><span>{t('tokenUsage.callStatus')}</span><select className="theme-select" value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}><option value="">{t('tokenUsage.allStatuses')}</option><option value="succeeded">{t('tokenUsage.succeeded')}</option><option value="failed">{t('tokenUsage.failed')}</option><option value="cancelled">{t('tokenUsage.cancelled')}</option></select></label>
            <label><span>{t('tokenUsage.usageStatus')}</span><select className="theme-select" value={filters.usage_status} onChange={(event) => updateFilter('usage_status', event.target.value)}><option value="">{t('tokenUsage.allUsageStatuses')}</option><option value="reported">{t('tokenUsage.reported')}</option><option value="unavailable">{t('tokenUsage.unavailable')}</option></select></label>
          </div>
        </details>
        <button type="button" className="token-icon-button" title={t('tokenUsage.reset')} aria-label={t('tokenUsage.reset')} onClick={resetFilters}>
          <FilterX size={18} />
        </button>
      </section>

      {error && <div className="status-banner status-banner--danger token-error">{error}</div>}

      <section className="token-metric-band" aria-label={t('tokenUsage.summary')}>
        {metrics.map(([key, label, value]) => (
          <div className="token-metric" key={key}>
            <span>{label}</span>
            <strong>{loading && !overview ? '...' : tokenValue(value, numberFormatter)}</strong>
          </div>
        ))}
        <div className="token-metric token-metric--coverage">
          <span>{t('tokenUsage.coverage')}</span>
          <strong>{coverage.toFixed(1)}%</strong>
          <div className="token-coverage-track"><span style={{ width: `${Math.min(coverage, 100)}%` }} /></div>
        </div>
      </section>

      <div className={`token-analysis-grid ${loading ? 'is-loading' : ''}`}>
        <section className="token-section token-trend-section">
          <div className="token-section__header">
            <h2>{t('tokenUsage.trend')}</h2>
            <span className="token-section__meta">{getTimezone()}</span>
          </div>
          <div className="token-chart" data-testid="token-usage-chart">
            {overview?.timeline?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={overview.timeline} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tokenInputFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#43d6c5" stopOpacity={0.3}/><stop offset="95%" stopColor="#43d6c5" stopOpacity={0.02}/></linearGradient>
                    <linearGradient id="tokenOutputFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#cfac67" stopOpacity={0.34}/><stop offset="95%" stopColor="#cfac67" stopOpacity={0.02}/></linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(129, 166, 186, 0.12)" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fill: '#8095a5', fontSize: 12 }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tickFormatter={compactNumber} tick={{ fill: '#8095a5', fontSize: 12 }} tickLine={false} axisLine={false} width={48} />
                  <Tooltip formatter={(value, name) => [numberFormatter.format(value), t(`tokenUsage.${name}`)]} contentStyle={{ background: '#0e2133', border: '1px solid rgba(129, 166, 186, .3)', borderRadius: 6 }} />
                  <Legend formatter={(value) => t(`tokenUsage.${value}`)} />
                  <Area type="monotone" dataKey="input_tokens" name="inputTokens" stroke="#43d6c5" fill="url(#tokenInputFill)" strokeWidth={2} />
                  <Area type="monotone" dataKey="output_tokens" name="outputTokens" stroke="#cfac67" fill="url(#tokenOutputFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="token-empty">{t('tokenUsage.noData')}</div>}
          </div>
        </section>

        <section className="token-section token-breakdown-section">
          <div className="token-section__header">
            <h2>{t('tokenUsage.breakdown')}</h2>
          </div>
          <div className="token-dimension-tabs" role="tablist" aria-label={t('tokenUsage.breakdown')}>
            {DIMENSIONS.map((item) => (
              <button key={item} type="button" role="tab" aria-selected={dimension === item} className={dimension === item ? 'is-active' : ''} onClick={() => { setDimension(item); setEventPage(1); }}>
                {t(`tokenUsage.dimensions.${item}`)}
              </button>
            ))}
          </div>
          <div className="token-breakdown-list">
            {breakdown.items?.length ? breakdown.items.map((item) => (
              <div className="token-breakdown-row" key={item.key}>
                <div className="token-breakdown-row__label"><strong title={getBreakdownLabel(item)}><bdi dir="auto">{getBreakdownLabel(item)}</bdi></strong><span>{item.secondary_label || `${item.request_count} ${t('tokenUsage.requestsShort')}`}</span></div>
                <div className="token-breakdown-row__value"><strong>{numberFormatter.format(item.total_tokens)}</strong><span>{t('tokenUsage.tokens')}</span></div>
                <div className="token-breakdown-row__bar"><span style={{ width: `${Math.max(2, (item.total_tokens / maxBreakdown) * 100)}%` }} /></div>
              </div>
            )) : <div className="token-empty">{t('tokenUsage.noData')}</div>}
          </div>
        </section>
      </div>

      <section className="token-section token-ledger-section">
        <div className="token-section__header">
          <h2>{t('tokenUsage.ledger')}</h2>
          <span className="token-section__meta">{numberFormatter.format(events.total || 0)} {t('tokenUsage.callsUnit')}</span>
        </div>
        <div className="token-ledger-shell">
          <table className="data-table token-ledger-table">
            <thead><tr><th>{t('tokenUsage.time')}</th><th>{t('tokenUsage.requestAndConversation')}</th><th>{t('tokenUsage.attribution')}</th><th>{t('tokenUsage.modelAndPurpose')}</th><th>{t('tokenUsage.input')}</th><th>{t('tokenUsage.output')}</th><th>{t('tokenUsage.total')}</th><th>{t('tokenUsage.status')}</th><th aria-label={t('tokenUsage.details')} /></tr></thead>
            <tbody>
              {events.items?.map((event) => (
                <tr key={event.call_id} className="token-ledger-row" tabIndex="0" onClick={() => setSelectedEvent(event)} onKeyDown={(keyEvent) => { if (keyEvent.key === 'Enter') setSelectedEvent(event); }}>
                  <td><span className="table-title">{dateFormatter.format(new Date(event.started_at))}</span><span className="table-meta">{Math.round(event.duration_ms)} ms</span></td>
                  <td><span className="table-token">{event.request_id.slice(0, 8)}</span><span className="table-meta">{event.conversation_id ? event.conversation_id.slice(0, 12) : t('tokenUsage.unassigned')}</span></td>
                  <td><span className="table-title"><bdi dir="auto">{formatPatientName(event, locale, event.patient_name || (event.patient_id ? `#${event.patient_id}` : t('tokenUsage.unassigned')))}</bdi></span><span className="table-meta">{[event.clinic_name, event.station_name].filter(Boolean).join(' / ') || t('tokenUsage.unassigned')}</span></td>
                  <td><span className="table-title">{event.model_name}</span><span className="table-meta">{event.provider} / {event.purpose}</span></td>
                  <td>{tokenValue(event.input_tokens, numberFormatter)}</td><td>{tokenValue(event.output_tokens, numberFormatter)}</td><td><strong>{tokenValue(event.total_tokens, numberFormatter)}</strong></td>
                  <td><span className={`status-pill token-status token-status--${event.usage_status === 'unavailable' ? 'unavailable' : event.status}`}>{event.usage_status === 'unavailable' ? t('tokenUsage.unavailable') : t(`tokenUsage.${event.status}`)}</span></td>
                  <td><ChevronRight size={17} aria-hidden="true" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!events.items?.length && <div className="token-empty">{t('tokenUsage.noData')}</div>}
        </div>
        {events.total > 0 && (
          <Pagination currentPage={eventPage} totalPages={totalPages} totalCount={events.total} pageSize={pageSize} onPageChange={setEventPage} onPageSizeChange={(size) => { setPageSize(size); setEventPage(1); }} pageSizeOptions={[10, 25, 50, 100]} showQuickJumper={false} />
        )}
      </section>

      {selectedEvent && (
        <div className="token-drawer-backdrop" role="presentation" onMouseDown={() => setSelectedEvent(null)}>
          <aside className="token-detail-drawer" role="dialog" aria-modal="true" aria-label={t('tokenUsage.details')} onMouseDown={(event) => event.stopPropagation()}>
            <div className="token-detail-drawer__header"><h2>{t('tokenUsage.details')}</h2><button type="button" className="token-icon-button" aria-label={t('tokenUsage.close')} onClick={() => setSelectedEvent(null)}><X size={19} /></button></div>
            <div className="token-detail-drawer__metrics"><div><span>{t('tokenUsage.totalTokens')}</span><strong>{tokenValue(selectedEvent.total_tokens, numberFormatter)}</strong></div><div><span>{t('tokenUsage.duration')}</span><strong>{Math.round(selectedEvent.duration_ms)} ms</strong></div><div><span>{t('tokenUsage.firstToken')}</span><strong>{selectedEvent.first_token_ms == null ? '--' : `${Math.round(selectedEvent.first_token_ms)} ms`}</strong></div></div>
            {[
              ['call', t('tokenUsage.callId'), selectedEvent.call_id],
              ['request', t('tokenUsage.requestId'), selectedEvent.request_id],
              ['conversation', t('tokenUsage.conversation'), selectedEvent.conversation_id],
              ['providerRequest', t('tokenUsage.providerRequestId'), selectedEvent.provider_request_id]
            ].map(([key, label, value]) => (
              <div className="token-detail-id" key={key}><span>{label}</span><code>{value || '--'}</code>{value && <button type="button" title={t('tokenUsage.copy')} aria-label={t('tokenUsage.copy')} onClick={() => copyValue(key, value)}>{copied === key ? <Check size={15} /> : <Clipboard size={15} />}</button>}</div>
            ))}
            <dl className="token-detail-list">
              <div><dt>{t('tokenUsage.endpoint')}</dt><dd>{selectedEvent.endpoint}</dd></div><div><dt>{t('tokenUsage.purpose')}</dt><dd>{selectedEvent.purpose}</dd></div><div><dt>{t('tokenUsage.provider')}</dt><dd>{selectedEvent.provider}</dd></div><div><dt>{t('tokenUsage.model')}</dt><dd>{selectedEvent.model_name}</dd></div><div><dt>{t('tokenUsage.callType')}</dt><dd>{selectedEvent.call_type}</dd></div><div><dt>{t('tokenUsage.callStatus')}</dt><dd>{t(`tokenUsage.${selectedEvent.status}`)}</dd></div><div><dt>{t('tokenUsage.inputTokens')}</dt><dd>{tokenValue(selectedEvent.input_tokens, numberFormatter)}</dd></div><div><dt>{t('tokenUsage.outputTokens')}</dt><dd>{tokenValue(selectedEvent.output_tokens, numberFormatter)}</dd></div><div><dt>{t('tokenUsage.cacheRead')}</dt><dd>{tokenValue(selectedEvent.cache_read_tokens, numberFormatter)}</dd></div><div><dt>{t('tokenUsage.cacheCreation')}</dt><dd>{tokenValue(selectedEvent.cache_creation_tokens, numberFormatter)}</dd></div><div><dt>{t('tokenUsage.reasoning')}</dt><dd>{tokenValue(selectedEvent.reasoning_tokens, numberFormatter)}</dd></div><div><dt>{t('tokenUsage.errorType')}</dt><dd>{selectedEvent.error_type || '--'}</dd></div>
            </dl>
            <div className="token-raw-usage"><span>{t('tokenUsage.rawUsage')}</span><pre>{JSON.stringify(selectedEvent.raw_usage || {}, null, 2)}</pre></div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default TokenUsageDashboard;
